import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { StageDefine } from '../components/studio/StageDefine';

/** Words that leave a title dangling if it ends on one ("...game where I"). */
const DANGLING = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'so', 'that', 'which', 'where', 'when', 'while', 'who',
  'with', 'for', 'to', 'of', 'in', 'on', 'at', 'by', 'from', 'as', 'i', 'my', 'me', 'you', 'your',
  'it', 'its', 'is', 'are', 'was', 'before', 'after', 'until', 'if', 'then', 'about',
]);

/** Derive a short, human title from the idea — the first clause, cut on a word that can end one. */
function titleFrom(idea: string): string {
  const firstClause = idea.split(/[.!?\n,;:]/)[0].trim();
  const words = firstClause.replace(/^(a|an|the)\s+/i, '').split(/\s+/).slice(0, 7);
  while (words.length > 2 && DANGLING.has(words[words.length - 1].toLowerCase())) words.pop();
  const title = words.join(' ').slice(0, 120) || 'Untitled';
  return title.charAt(0).toUpperCase() + title.slice(1);
}

/**
 * DEFINE as its own route (`/projects/new`).
 *
 * The project row is created only when the learner actually submits an idea. Creating it on the
 * "New build" click instead littered My Creations with empty "Untitled" projects every time someone
 * opened the screen and backed out — and it forced the studio to mount against a project with no
 * description, which is what produced the flash of editor chrome before the question appeared.
 */
export function NewBuildPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async (idea: string) => {
    setBusy(true);
    setError(null);
    try {
      const { project } = await api.post<{ project: { id: string } }>('/box/projects', {
        title: titleFrom(idea),
        description: idea,
      });
      // replace: Back from the studio should return to My Creations, not to a spent Define screen
      navigate(`/projects/${project.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start that build. Please try again.');
      setBusy(false);
    }
  };

  return (
    <StageDefine
      name={user?.displayName ?? null}
      busy={busy}
      error={error}
      onStart={idea => void start(idea)}
    />
  );
}
